import BasicForm from "../BasicForm";
import {IAppComponentProps} from "../../AppComponent";
import {DOMAttributes} from "react";
import {
    dateToLocalISOString,
    generateHexString,
    isNumeric,
    isString,
    unixToDate
} from "../../../../ts/common/SiteHelper";
import {preparedPointer} from "../helper/PointerHelper";
import FileReplaceVO, {ImageFileReplaceVO} from "./vo/FileReplaceVO";
import MapLocationVO from "./vo/MapLocationVO";

export type FormItemType = "string" | "float" | "int" | "boolean" | "date" | "file" | "map_location" | "group" | "repeater" | "array" | "none";
export type FormItemCollectionType = "single" | "group_object" | "group_array" | "id_array";

interface IFormErrorMessages
{
    required?: string
}

export class FormItem
{
    itemID: string;
    fieldID: string;
    pointer: IPointer;

    name: string;
    type: FormItemType = "none";
    props:IFormItemProps;
    defaultValue: any = null;
    value: any = null;
    previousValue: any = null;
    required: boolean = false;
    errorMessages: IFormErrorMessages = {};
    doNotSubmit: boolean = false;

    errorText:string = "";

    isPlaceholder: boolean = false;
    hasLocalError: boolean = false;


    active:boolean = false;
    canSubmit:boolean = false;
    willSubmitEmpty:boolean = false;
    preventSubmitEmpty:boolean = false;

    returnEmpty: boolean = false;
    activateOnSubmit:boolean = false;
    activatedByItemID:string = "";
    willDeleteItemIDs:string[] = [];
    deleteOnSubmit:boolean = false;
    initialized:boolean = false;
    preventNullUpdate:boolean = false;

    constructor(props: IFormItemProps)
    {
        this.pointer = preparedPointer(props.pointer);
        this.itemID = this.pointer.itemID!;
        this.fieldID = this.pointer.fieldID;

        this.name = props.name;
        this.props = props;
    }

    reapplyPointer(pointer: IPointer){
        this.pointer = pointer;
        this.itemID = this.pointer.itemID!;
        this.fieldID = this.pointer.fieldID;
        this.props.pointer = pointer;
    }

    update(props: IFormItemProps):void
    {
        this.props = props;
        this.isPlaceholder = props.isPlaceholder ?? false;
        if(!props.isPlaceholder){
            const propsValue = props.value ?? null;
            if(!this.initialized){
                this.initialized = true;
                if(propsValue !== null){
                    this.setValue(propsValue);
                    this.defaultValue = propsValue;
                    this.previousValue = propsValue;
                }
            } else {
                const preventDefaultUpdate = this.props.isDefaultPermanent ?? (this.preventNullUpdate && (propsValue === null));
                if(!preventDefaultUpdate || (this.defaultValue === null)){
                    const currentValue = this.value;
                    this.setValue(propsValue);
                    if(this.compareValues(this.getCompareValue(this.value),this.getCompareValue(this.defaultValue)))
                    {
                        this.value = currentValue;
                    } else {
                        this.defaultValue = this.value;
                    }
                }
            }
            this.activateOnSubmit = props.activateOnSubmit ?? false;
            this.activatedByItemID = props.activatedByItemID ?? "";
            this.preventSubmitEmpty = props.preventSubmitEmpty ?? false;
            this.willDeleteItemIDs = props.willDeleteItemIDs ? (Array.isArray(props.willDeleteItemIDs) ? props.willDeleteItemIDs : [props.willDeleteItemIDs]) : [];
        }
        this.name = props.name;
        this.required = props.required ?? false;
        this.errorMessages = props.errorMessages ?? {};
        this.doNotSubmit = props.doNotSubmit ?? false;
    }

    isEmpty():boolean
    {
        return this.value === null || this.value === undefined || this.value === ""
    }

    hasValue(): boolean
    {
        return !this.isEmpty();
    }

    compareValues(valueA:any, valueB:any, _:boolean = false):any
    {
        return valueA === valueB;
    }

    getCompareValue(ofValue:any):any
    {
        return ofValue;
    }

    getSubmitValue():any
    {
        if(this.preventSubmitEmpty){
            if(this.isEmpty()){
                return null;
            }
        }
        return this.canSubmit ? (!this.willSubmitEmpty ? (this.value ?? "") : "") : null;
    }

    getCastValue():any
    {
        return this.active ? this.value : null;
    }

    setValue(value: any):boolean
    {
        if(!this.compareValues(this.getCompareValue(value), this.getCompareValue(this.value))){
            this.previousValue = this.value;
            this.value = value;
            return true;
        }
        return false;
    }

    canReset():boolean
    {
        return (this.getCompareValue(this.value) !== this.getCompareValue(this.defaultValue));
    }

    doReset():void
    {
        this.value = this.defaultValue;
    }
}

export class TextFormItem extends FormItem
{

}

export class StringFormItem extends TextFormItem
{
    type: FormItemType = "string";
    props:IStringFormItemProps;
    defaultValue: string|null = null;
    value: string|null = null;
    previousValue: string|null = null;

    constructor(props: IStringFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    getSubmitValue(): string|null
    {
        return super.getSubmitValue();
    }

    getCastValue(): string
    {
        return this.value ?? "";
    }

    setValue(value: string|null): boolean
    {
        return super.setValue(value);
    }
}

export class FloatFormItem extends TextFormItem
{
    type: FormItemType = "float";
    props:IFloatFormItemProps;
    defaultValue: number|null = null;
    value: number|null = null;
    previousValue: number|null = null;

    constructor(props: IFloatFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    getSubmitValue(): number|string|null
    {
        return super.getSubmitValue();
    }

    getCastValue():number
    {
        return this.value ?? 0;
    }

    hasValue(): boolean
    {
        return !this.isEmpty() && (this.value !== 0);
    }

    setValue(value: number|string|null):boolean
    {
        if(value !== null){
            if(typeof value !== 'number'){
                value = parseFloat(value);
            }
            value = !isNaN(value) ? value: null;
        }
        return super.setValue(value);
    }
}

export class IntFormItem extends TextFormItem
{
    type: FormItemType = "int";
    props:IIntFormItemProps;
    defaultValue: number|null = null;
    value: number|null = null;
    previousValue: number|null = null;

    constructor(props: IIntFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    getSubmitValue(): number|string|null
    {
        return super.getSubmitValue();
    }

    getCastValue():number
    {
        return this.value ?? 0;
    }

    hasValue(): boolean
    {
        return !this.isEmpty() && (this.value !== 0);
    }

    setValue(value: number|string|null): boolean
    {
        if(value !== null){
            if(typeof value !== 'number'){
                value = parseInt(value);
            } else {
                value = Math.round(value);
            }
            value = !isNaN(value) ? value: null;
        }
        return super.setValue(value);
    }

}

export class BooleanFormItem extends FormItem
{
    type: FormItemType = "boolean";
    props:IBooleanFormItemProps;
    defaultValue: boolean|null = null;
    value: boolean|null = null;
    previousValue: boolean|null = null;

    constructor(props: IBooleanFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    getSubmitValue(): boolean|string|null
    {
        return super.getSubmitValue();
    }

    getCastValue():boolean
    {
        return this.value ?? false;
    }

    hasValue(): boolean
    {
        return !this.isEmpty() && (this.value !== false);
    }

    setValue(value: boolean|null): boolean
    {
        return super.setValue(value);
    }

}

export class ArrayFormItem extends FormItem
{
    type: FormItemType = "array";
    props:IArrayFormItemProps;
    defaultValue: string[] = [];
    value: string[] = [];
    previousValue: string[] = [];
    validValues: string[]|null = null;
    preventNullUpdate:boolean = true;

    constructor(props: IArrayFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }


    update(props: IArrayFormItemProps)
    {
        super.update(props);
        this.validValues = null;
        if(props.validValues){
            this.validValues = [];
            for(let childValue of props.validValues){
                this.validValues.push(childValue+"");
            }
        }
    }

    getSubmitValue(): string[]
    {
        return super.getSubmitValue();
    }

    getCastValue():string[]
    {
        return this.value ?? [];
    }

    hasValue(): boolean
    {
        return !this.isEmpty() && (!!this.value?.length);
    }

    isEmpty(): boolean
    {
        return !this.value.length;
    }

    setValue(value: string[]): boolean
    {
        value = Array.isArray(value) ? value : (((value ?? null) !== null) ? [value] : []);
        for(let i = 0; i < value.length; i++){
            const childValue = value[i].toString();
            value[i] = childValue;
            if(value.indexOf(childValue) < i){
                value.splice(i, 1);
                i --;
            }
        }
        return super.setValue(value);
    }

    compareValues(valueA: string[], valueB: string[]): any
    {
        if(valueA.length !== valueB.length){
            return false;
        }
        for(let childValue of valueA){
            if(!valueB.includes(childValue)){
                return false;
            }
        }

        return true;
    }

    addChildValue(childValue:string): boolean
    {
        if(this.validValues && !this.validValues.includes(childValue)){
            return false;
        }
        if(this.value.includes(childValue)){
            return false;
        }
        this.value.push(childValue);
        return true;
    }

    removeChildValue(childValue:string): boolean
    {
        let index = this.value.indexOf(childValue);
        let didRemove = false;
        while(index > -1){
            this.value.splice(index, 1);
            index = this.value.indexOf(childValue);
            didRemove = true;
        }
        return didRemove
    }
}

export class DateFormItem extends FormItem
{
    type: FormItemType = "date";
    props:IDateFormItemProps;
    defaultValue: Date|null = null;
    value: Date|null = null;
    previousValue: Date|null = null;

    constructor(props: IDateFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    isoStringDate = () =>
    {
        return this.value ? dateToLocalISOString(this.value) : "";
    }

    getSubmitValue(): number|string|null
    {
        if(!this.canSubmit){
            return null;
        }
        const value:Date|null = super.getSubmitValue();
        return value ? Math.round((value.getTime()) / 1000) : "";
    }


    getCompareValue(ofValue:any): number
    {
        return (ofValue instanceof Date ) ? Math.round((ofValue.getTime()) / 1000) : 0;
    }

    getCastValue():Date|null
    {
        return this.value;
    }

    setValue(value: Date|string|number|null): boolean
    {
        let useValue: Date|null = null;
        if(value){
            if(value instanceof Date){
                useValue = value;
            } else if(isNumeric(value)){
                useValue = unixToDate(value!.toString())
            } else if(isString(value)){
                useValue = new Date(value!.toString());
            }
        }
        return super.setValue(useValue);
    }
}

export class FileFormItem extends FormItem
{
    type: FormItemType = "file";
    props:IFileFormItemProps;
    defaultValue: FileReplaceVO;
    value: FileReplaceVO;
    previousValue: FileReplaceVO;
    preventNullUpdate:boolean = true;

    constructor(props: IFileFormItemProps)
    {
        super(props);
        this.value = new FileReplaceVO(null);
        this.defaultValue = this.value;
        this.previousValue = this.value;
        this.props = props;
        this.update(props);
    }

    compareValues(valueA: FileReplaceVO, valueB: FileReplaceVO): any
    {
        if((valueA.fileType !== valueB.fileType) ||
            (valueA.savedFileVOs.length !== valueB.savedFileVOs.length) ||
            (valueA.formDataVOs.length !== valueB.formDataVOs.length) ||
            (valueA.deleteFileIDs.length !== valueB.deleteFileIDs.length)){
            return false;
        }
        for(let index in valueA.savedFileVOs){
            const fileVO = valueA.savedFileVOs[index];
            if(fileVO.id !== valueB.savedFileVOs[index].id){
                return false;
            }
        }
        for(let index in valueA.formDataVOs){
            const formDataVO = valueA.formDataVOs[index];
            if(formDataVO !== valueB.formDataVOs[index]){
                return false;
            }
        }
        for(let fileID of valueA.deleteFileIDs){
            if(!valueB.deleteFileIDs.includes(fileID)){
                return false;
            }
        }

        return true;
    }

    getSubmitValue(): FileReplaceVO
    {
        return super.getSubmitValue();
    }

    getCastValue():FileReplaceVO
    {
        return this.value;
    }

    isEmpty(): boolean
    {
        return this.value.isEmpty();
    }

    willAddFiles(): boolean
    {
        return !!this.value.formDataVOs.length;
    }

    willRemoveFiles(): boolean
    {
        for(let fileVO of this.value.savedFileVOs){
            if(this.value.deleteFileIDs.includes(fileVO.id)){
                return true;
            }

        }
        return false;
    }

    willFilesUpdate(): boolean
    {
        return this.willAddFiles() || this.willRemoveFiles();
    }

    canReset(): boolean
    {
        return !!this.value.formDataVOs.length || !!this.value.deleteFileIDs.length;
    }

    doReset(): void
    {
        const value = this.value.clone();
        value.formDataVOs = [];
        value.deleteFileIDs = [];
        this.setValue(value);
    }
}

export class MapLocationFormItem extends FormItem
{
    type: FormItemType = "map_location";
    props:IMapLocationFormItemProps;
    defaultValue: MapLocationVO;
    value: MapLocationVO;
    previousValue: MapLocationVO;
    preventNullUpdate:boolean = true;

    constructor(props: IMapLocationFormItemProps)
    {
        super(props);
        this.value = new MapLocationVO();
        this.defaultValue = this.value;
        this.previousValue = this.value;
        this.props = props;
        this.update(props);
    }

    compareValues(valueA: MapLocationVO, valueB: MapLocationVO): any
    {

        return (valueA.lat === valueB.lat) &&
            (valueA.lng === valueB.lng) &&
            (valueA.boundsWidth === valueB.boundsWidth) &&
            (valueA.boundsHeight === valueB.boundsHeight) &&
            (valueA.hidden === valueB.hidden)
    }

    getSubmitValue(): MapLocationVO
    {
        return super.getSubmitValue();
    }

    getCastValue():MapLocationVO
    {
        return this.value;
    }

    isEmpty(): boolean
    {
        return this.value.isEmpty();
    }
}

export class GroupFormItem extends FormItem
{
    type: FormItemType = "group";

    props:IGroupFormItemProps;
    isChildrenEmpty:boolean = false;

    constructor(props: IGroupFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }


    update(props: IGroupFormItemProps)
    {
        super.update(props);
        this.isChildrenEmpty = !!this.props.isChildrenEmpty;
    }

    isEmpty(): boolean
    {
        return this.isChildrenEmpty;
    }
}


export class RepeaterFormItem extends FormItem
{
    type: FormItemType = "repeater";

    props:IRepeaterFormItemProps;

    defaultValue: string[] = [];
    value: string[] = [];
    previousValue: string[] = [];
    preventNullUpdate:boolean = true;

    currentRowIDs: string[] = [];

    rowMin:number = 0;
    rowMax:number|null = null;

    constructor(props: IRepeaterFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    update(props: IRepeaterFormItemProps)
    {
        super.update(props);
        const rowMin = Math.round(props.rowMin ?? 0);
        this.rowMin = Math.max(rowMin, 0);
        const rowMax = props.rowMax ? Math.round(props.rowMax) : null;
        this.rowMax = rowMax !== null ? Math.max(rowMax, this.rowMin) : null;

        this.applyMinMax();

    }

    compareValues(valueA: string[], valueB: string[], loose:boolean = false): any
    {
        if(valueA.length !== valueB.length){
            return false;
        }
        if(!loose){
            for(let i = 0; i < valueA.length; i++){
                if(valueA[i] !== valueB[i]){
                    return false;
                }
            }
        }
        return true;
    }

    isFixedAmount()
    {
        return this.rowMin === this.rowMax;
    }

    canAddRow()
    {
        return (this.rowMax === null) || (this.currentRowIDs.length < this.rowMax)
    }

    canReset()
    {
        return !this.compareValues(this.currentRowIDs, this.value);
    }

    canRemoveRow()
    {
        return this.currentRowIDs.length > this.rowMin;
    }

    addRow()
    {
        if(!this.canAddRow()){
            return
        }
        this.currentRowIDs.push(generateHexString());
    }

    isEmpty(): boolean
    {
        return !this.currentRowIDs.length;
    }


    setValue(value: any): boolean
    {
        const didChange =  super.setValue(value);
        if(didChange){
            this.currentRowIDs = this.value.slice();
            this.applyMinMax();
        }
        return didChange;
    }

    applyMinMax(){
        const userRowMax = this.rowMax ?? this.currentRowIDs.length;
        while(this.currentRowIDs.length < this.rowMin){
            this.addRow()
        }
        if(this.currentRowIDs.length > userRowMax){
            this.currentRowIDs.length = userRowMax;
        }
    }

    removeRow(rowID: any)
    {
        if(!this.canRemoveRow()){
            return
        }
        const index = this.currentRowIDs.indexOf(rowID);
        if(index >= 0){
            this.currentRowIDs.splice(index, 1);
        }
    }
}

export class NoneFormItem extends FormItem
{
    type: FormItemType = "none";
    props:INoneFormItemProps;
    value: null = null;

    constructor(props: INoneFormItemProps)
    {
        super(props);
        this.props = props;
        this.update(props);
    }

    getSubmitValue():any
    {
        return null;
    }
}

export interface IPointer
{
    fieldID: string;
    itemID?: string;
    index?: number|null;
    collectionType?:FormItemCollectionType;
    childPointer?:IPointer|null;
}

export interface IFormItemProps
{
    pointer: IPointer|string,
    collectionType?:FormItemCollectionType
    groupID?: string|null,
    name: string,
    type?: FormItemType,
    value?: string|number|boolean|Date|FileReplaceVO|MapLocationVO|string[]|null,
    required?: boolean,
    errorMessages?: IFormErrorMessages,
    doNotSubmit?: boolean,
    isPlaceholder?: boolean,
    isDefaultPermanent?: boolean,
    activateOnSubmit?: boolean
    preventSubmitEmpty?: boolean
    activatedByItemID?:string|null
    willDeleteItemIDs?:string|string[]|null
    returnEmptyIfDeactivatedByItemID?:string|null
}

export interface IStringFormItemProps extends IFormItemProps
{
    type?: "string",
    value?: string|null,
}

export interface IFloatFormItemProps extends IFormItemProps
{
    type?: "float",
    value?: number|null,
}

export interface IIntFormItemProps extends IFormItemProps
{
    type?: "int",
    value?: number|null,
}

export interface IBooleanFormItemProps extends IFormItemProps
{
    type?: "boolean",
    value?: boolean|null,
}

export interface IArrayFormItemProps extends IFormItemProps
{
    type?: "array",
    value?: string[]|null,
    validValues?: string[]|null,
}

export interface IDateFormItemProps extends IFormItemProps
{
    type?: "date",
    value?: Date|string|number|null,
}

export interface IFileFormItemProps extends IFormItemProps
{
    type?: "file",
    value?: FileReplaceVO
}

export interface IMapLocationFormItemProps extends IFormItemProps
{
    type?: "map_location",
    value?: MapLocationVO
}

export interface IImageFileFormItemProps extends IFileFormItemProps
{
    value?: ImageFileReplaceVO
}

export interface IRepeaterFormItemProps extends IFormItemProps
{
    type?: "repeater",
    value?:string[];
    rowMin?:number;
    rowMax?:number;
    createLineData?:(formItem:RepeaterFormItem) => any;
}

export interface IGroupFormItemProps extends IFormItemProps
{
    type?: "group"
    isChildrenEmpty?:boolean
}

export interface INoneFormItemProps extends IFormItemProps
{
    type?: "none",
    value?: undefined,
}

export interface IFormItems
{
    [key: string]: FormItem;
}

export interface IFormElement extends DOMAttributes<any>
{
    hidden?: boolean;
}

export interface IFormItemElement extends IFormElement
{
    form:BasicForm;
    formItemProps:IFormItemProps;
    label?: string,
    instructions?: string,
    onItemChange?: (((formItemID: string) => void))|null;
    hidden?:boolean;
}

export interface IFloatFormItemElement extends IFormItemElement
{
    formItemProps:IFloatFormItemProps;
}

export interface IFormProps extends IAppComponentProps
{
    onAction?: () => void;
    onSubmit?: (submitType?: string) => void;
    onCancel?: () => void;
    onDisabledStateChange?: (disabled:boolean) => void;
    isControlledExternally?: boolean;
}